import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";

import Grid2 from "@mui/material/Unstable_Grid2";
import { MdAddchart, MdDonutLarge, MdInsertChartOutlined, MdTrendingUp } from "react-icons/md";
import useCallAxios from "../../../../Hooks/useCallAxios";
import { useFilter } from "../../../../Hooks/useFilter";
import { Loading } from "../../../Atoms";
import { TableFilter } from "../../Filters";
import { Pagination } from "../../TablePagination";
import { InvoiceCard } from "../InvoiceCard";
import { InvoiceRow } from "../InvoiceRow";
import * as styles from "../styles";

interface InvoiceListProps {
  setLoading?: (val: boolean) => void;
  enabled?: boolean;
}

export const InvoiceList = ({ setLoading, enabled }: InvoiceListProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [transactions, setTransactions] = useState({} as any);
  const [dashboards, setDashboards] = useState({} as any);

  const context = "invoices";
  const contextDash = "invoiceDashboard";

  const { getFilterObj } = useFilter();
  const invoicesFilterObj = getFilterObj("invoices");

  const {
    fetchGET: fetchInvoices,
    requestLoading: loading,
    requestData: invoices,
  } = useCallAxios({ context });

  const {
    fetchGET: fetchDashboard,
    requestLoading: loadingDash,
    requestData: dashboardData,
  } = useCallAxios({ context: contextDash });

  const handleSetToFirstPage = () => {
    setPage(0);
  };

  const emptyRows =
    page >= 0
      ? Math.max(0, (1 + page) * rowsPerPage - (transactions?.pagination?.totalItems || 0))
      : 0;

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!enabled) return;
    fetchInvoices({
      queryParameters: {
        pageSize: rowsPerPage,
        page: page + 1,

        ...invoicesFilterObj,
      },
    });
    fetchDashboard({
      queryParameters: {
        ...invoicesFilterObj,
      },
    });
  }, [invoicesFilterObj, rowsPerPage, page, enabled]);

  useEffect(() => {
    setTransactions((invoices as any)?.data);
  }, [invoices]);

  useEffect(() => {
    setDashboards((dashboardData as any)?.data);
  }, [dashboardData]);

  useEffect(() => {
    if (setLoading) {
      setLoading(loading.GET);
    }
  }, [loading.GET]);

  const statusCards = [
    {
      title: 'Total Enviado',
      key: 'Enviada',
      icon: MdInsertChartOutlined,
    },
    {
      title: 'Total Antecipado',
      key: 'Antecipada',
      icon: MdDonutLarge,
    },
    {
      title: 'Total Aprovado',
      key: 'Aprovada',
      icon: MdAddchart,
    }
  ];

  return (
    <>
      <TableFilter
        tableName="invoices"
        setPageToFirst={handleSetToFirstPage}
      />

      <Grid2 spacing={2} container sx={{ mb: 2 }}>
        {statusCards.map((card) => (
          <Grid2 key={card.key} xs={12} lg={3} md={6}>
            <InvoiceCard
              title={card.title}
              loading={loadingDash.GET}
              icon={card.icon}
              value={
                dashboards?.statusCounts?.find(
                  (item: any) => item.status === card.key
                )?.count || 0
              }
            />
          </Grid2>
        ))}
        <Grid2 xs={12} lg={3} md={6}>
          <InvoiceCard
            title="Limite de Antecipação"
            loading={loadingDash.GET}
            icon={MdTrendingUp}
            value={
              (dashboards?.limit || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              })
            }
          />
        </Grid2>
      </Grid2>

      <styles.StyledGridContainer container>
        <styles.Content>
          <styles.StyledGrid item xs={12}>
            <Container disableGutters>
              <TableContainer component={Paper} sx={{ maxHeight: "100vh" }}>
                <Table aria-label="tabela de antecipação de recebíveis" stickyHeader={true} >
                  <styles.HeaderTable>
                    <TableRow>
                      <TableCell style={{ width: 10 }}></TableCell>
                      <TableCell>Protocolo</TableCell>
                      <TableCell>Solicitação</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Destinatário</TableCell>
                      <TableCell>Número NF</TableCell>
                      <TableCell>Chave de Acesso</TableCell>
                    </TableRow>
                  </styles.HeaderTable>
                  <TableBody>
                    {loading.GET ? (
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={8}
                        >
                          <Loading />
                        </TableCell>
                      </TableRow>
                    ) : !loading.GET &&
                      transactions?.invoices?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <styles.TransactionsNotFoundWarn>
                            Nenhuma transação encontrada!
                          </styles.TransactionsNotFoundWarn>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions?.invoices?.map((invoice, index) => {
                        return (
                          <InvoiceRow
                            key={index}
                            transaction={invoice}
                            disabled={!enabled}
                          />
                        );
                      })
                    )}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  <Pagination
                    rowsPerPageOptions={[10, 25, 40]}
                    rowCount={(transactions?.pagination?.totalItems || 0)}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    label={"Itens por página"}
                  />
                </Table>
              </TableContainer>
            </Container>
          </styles.StyledGrid>
        </styles.Content>
      </styles.StyledGridContainer>
    </>
  );
};
